import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

export type DashboardChartColorTheme =
  | 'noc'
  | 'vibrant'
  | 'pastel'
  | 'corporate'
  | 'warm'
  | 'cool'
  | 'monochrome';

export interface DashboardChartThemeOption {
  id: DashboardChartColorTheme;
  label: string;
  description: string;
}

@Injectable({
  providedIn: 'root'
})
export class DashboardChartThemeService {
  private static readonly STORAGE_KEY = 'noc.dashboard.chartTheme';

  private activeTheme: DashboardChartColorTheme = 'vibrant';
  readonly changed$ = new Subject<DashboardChartColorTheme>();

  readonly options: DashboardChartThemeOption[] = [
    { id: 'noc', label: 'NOC (padrão do painel)', description: 'Roxo, turquesa e laranja do tema Paper.' },
    { id: 'vibrant', label: 'Vibrante', description: 'Cores saturadas, bom contraste entre séries.' },
    { id: 'pastel', label: 'Pastel', description: 'Tons suaves, ideal para muitas categorias.' },
    { id: 'corporate', label: 'Corporativo', description: 'Azuis e cinzas, visual executivo.' },
    { id: 'warm', label: 'Quente', description: 'Vermelhos, laranjas e amarelos.' },
    { id: 'cool', label: 'Frio', description: 'Azuis, verdes e roxos.' },
    { id: 'monochrome', label: 'Monocromático', description: 'Escala de cinza (estilo atual reduzido).' }
  ];

  constructor() {
    const stored = this.loadStored();
    if (stored) {
      this.activeTheme = stored;
    }
  }

  private readonly palettes: Record<DashboardChartColorTheme, string[]> = {
    noc: ['#6A5ACD', '#51CBCE', '#EF8157', '#FFB236', '#4CAF50', '#F44336', '#2196F3', '#9C27B0'],
    vibrant: [
      '#6366F1', '#06B6D4', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6',
      '#EC4899', '#14B8A6', '#F97316', '#3B82F6', '#84CC16', '#A855F7'
    ],
    pastel: [
      '#A5B4FC', '#99F6E4', '#FDE68A', '#BBF7D0', '#FECACA', '#DDD6FE',
      '#FBCFE8', '#BAE6FD', '#FED7AA', '#C7D2FE', '#D9F99D', '#E9D5FF'
    ],
    corporate: [
      '#1E3A5F', '#2563EB', '#0EA5E9', '#64748B', '#334155', '#0369A1',
      '#475569', '#0284C7', '#1D4ED8', '#94A3B8', '#0F766E', '#7C3AED'
    ],
    warm: ['#DC2626', '#EA580C', '#D97706', '#CA8A04', '#F97316', '#FB7185', '#F43F5E', '#B45309'],
    cool: ['#0D9488', '#0891B2', '#2563EB', '#4F46E5', '#7C3AED', '#059669', '#0284C7', '#1E40AF'],
    monochrome: ['#374151', '#6B7280', '#9CA3AF', '#4B5563', '#D1D5DB', '#1F2937', '#111827', '#F3F4F6']
  };

  getActiveTheme(): DashboardChartColorTheme {
    return this.activeTheme;
  }

  setActiveTheme(theme: DashboardChartColorTheme): void {
    if (this.palettes[theme]) {
      this.activeTheme = theme;
      this.persist(theme);
      this.changed$.next(theme);
    }
  }

  private loadStored(): DashboardChartColorTheme | null {
    try {
      const raw = sessionStorage.getItem(DashboardChartThemeService.STORAGE_KEY);
      if (raw && this.palettes[raw as DashboardChartColorTheme]) {
        return raw as DashboardChartColorTheme;
      }
    } catch {
      // ignore
    }
    return null;
  }

  private persist(theme: DashboardChartColorTheme): void {
    try {
      sessionStorage.setItem(DashboardChartThemeService.STORAGE_KEY, theme);
    } catch {
      // ignore
    }
  }

  getPalette(count = 8, theme?: DashboardChartColorTheme): string[] {
    const base = this.palettes[theme || this.activeTheme] || this.palettes.vibrant;
    if (count <= base.length) {
      return base.slice(0, count);
    }
    const out: string[] = [];
    for (let i = 0; i < count; i++) {
      out.push(base[i % base.length]);
    }
    return out;
  }
}
